import { goRequest } from '../utils/httpClientTs';

export type FoundationAvailableFund = {
  id: string;
  name: string;
  available_amount: number;
};

export type FoundationApplicationListItem = {
  id: string;
  academic_year: string;
  stage: string;
  student_first_name?: string;
  student_last_name?: string;
  student_email?: string;
  awarded_amount: number;
  currency: string;
  created_at: string;
  updated_at: string;
  waiting?: string[];
};

export const FOUNDATION_WAITING_LABELS: Record<string, string> = {
  trustee: 'Trustee review',
  eo: 'EO review',
  student: 'Student verification',
  school: 'School match',
  trustee_hold: 'Trustee hold',
  enrollment: 'Enrollment docs',
  final_approval: 'Final approval',
};

export const FOUNDATION_STAGE_LABELS: Record<string, string> = {
  draft: 'Draft',
  discarded: 'Discarded',
  gate_trustee_hold: 'Trustee hold',
  parallel_review: 'Parallel review',
  post_award_docs: 'Post-award docs',
  final_io_approval: 'Final IO approval',
  approved: 'Approved',
  cancelled: 'Cancelled',
  partially_paid: 'Partially paid',
  paid: 'Paid',
};

export type FoundationGates = {
  eligibility_gate_status: string;
  student_verification_status: string;
  trustee_review_status: string;
  io_review_status: string;
  enrollment_status: string;
};

export type FoundationCommitment = {
  id: string;
  fund_id: string;
  fund_name?: string;
  amount: number;
  status: string;
  academic_year: string;
};

export type FoundationInstitution = {
  id: string;
  match_status: string;
  accredited_institution_id?: string;
  provisional_name?: string;
  provisional_city?: string;
  provisional_state?: string;
  provisional_country?: string;
  provisional_website?: string;
  exception_reason?: string;
  matched_name?: string;
  matched_city?: string;
  matched_state_code?: string;
  matched_address_raw?: string;
};

export type FoundationReviewAction = {
  id: string;
  action_type: string;
  visibility: string;
  body?: string;
  actor_user_id: string;
  actor_name?: string;
  review_step_key?: string;
  created_at: string;
};

export type FoundationFormAnswer = {
  question_key: string;
  value_text?: string;
  answer_group_id: string;
};

export type FoundationFormAnswerInput = {
  question_key: string;
  value: string;
  answer_group_id?: string;
};

export type FoundationStudentContact = {
  student_address_1?: string;
  student_address_2?: string;
  student_city?: string;
  student_state_id?: string;
  student_postal_code?: string;
  student_country_id?: string;
  student_primary_phone?: string;
  student_primary_phone_type?: string;
  student_secondary_phone?: string;
  student_secondary_phone_type?: string;
};

export type FoundationApplicationDetail = FoundationApplicationListItem &
  FoundationStudentContact & {
    project_id: string;
    source: string;
    recommender_user_id?: string;
    student_user_id?: string;
    local_chapter_credit_id?: string;
    ach_required: number;
    installment_count?: number;
    notes?: string;
    gates?: FoundationGates;
    commitments?: FoundationCommitment[];
    institution?: FoundationInstitution | null;
    review_actions?: FoundationReviewAction[];
    answers_by_form_kind?: Record<string, FoundationFormAnswer[]>;
  };

export type FoundationDisbursement = {
  id: string;
  award_application_id: string;
  amount: number;
  status: string;
  scheduled_date?: string;
  payment_method?: string;
  payment_reference?: string;
  student_name?: string;
};

export type FoundationFormQuestion = {
  id: string;
  question_key: string;
  label: string;
  help_text?: string;
  answer_type: string;
  required: number;
  sort_order: number;
  parent_question_id?: string;
  is_conditional: number;
  conditional_value?: string;
  supports_repeating_groups: number;
  options_json?: Record<string, unknown>;
};

export type FoundationFormDefinition = {
  id: string;
  form_kind: string;
  academic_year: string;
  name: string;
  version: number;
  questions?: FoundationFormQuestion[];
};

export type FoundationInstitutionSearchResult = {
  id: string;
  name: string;
  city?: string;
  state_code?: string;
  location_type?: string;
  dapip_institution_id?: string;
};

const base = '/foundation';

export const listFoundationApplications = (params?: {
  stage?: string;
  academic_year?: string;
}): Promise<{ items: FoundationApplicationListItem[] }> => {
  const search = new URLSearchParams();
  if (params?.stage) search.set('stage', params.stage);
  if (params?.academic_year) search.set('academic_year', params.academic_year);
  const q = search.toString();
  return goRequest({
    url: `${base}/applications${q ? `?${q}` : ''}`,
    method: 'GET',
  });
};

export const getFoundationApplication = (
  id: string
): Promise<FoundationApplicationDetail> =>
  goRequest({ url: `${base}/applications/${id}`, method: 'GET' });

export const createFoundationDraft = (
  body: {
    academic_year?: string;
    student_first_name?: string;
    student_last_name?: string;
    student_email?: string;
    awarded_amount?: number;
  } & FoundationStudentContact
): Promise<FoundationApplicationDetail> =>
  goRequest({ url: `${base}/applications`, method: 'POST', data: body });

export const patchFoundationApplication = (
  id: string,
  body: Record<string, unknown>
): Promise<FoundationApplicationDetail> =>
  goRequest({ url: `${base}/applications/${id}`, method: 'PATCH', data: body });

export const submitFoundationRecommendation = (
  id: string
): Promise<FoundationApplicationDetail> =>
  goRequest({
    url: `${base}/applications/${id}/submit-recommendation`,
    method: 'POST',
  });

export const discardFoundationApplication = (
  id: string
): Promise<{ action: 'purged' | 'discarded'; id: string }> =>
  goRequest({
    url: `${base}/applications/${id}/discard`,
    method: 'POST',
  });

export const listFoundationAvailableFunds = (params?: {
  academic_year?: string;
}): Promise<{ items: FoundationAvailableFund[] }> => {
  const search = new URLSearchParams();
  if (params?.academic_year) search.set('academic_year', params.academic_year);
  const q = search.toString();
  return goRequest({
    url: `${base}/funds/available${q ? `?${q}` : ''}`,
    method: 'GET',
  });
};

export const postFoundationReviewAction = (
  id: string,
  body: { action_type: string; body?: string; visibility?: string }
): Promise<FoundationApplicationDetail> =>
  goRequest({
    url: `${base}/applications/${id}/review-actions`,
    method: 'POST',
    data: body,
  });

export const resolveFoundationTrusteeHold = (
  id: string,
  body: { approve: boolean; notes?: string }
): Promise<FoundationApplicationDetail> =>
  goRequest({
    url: `${base}/applications/${id}/gates/trustee-hold`,
    method: 'POST',
    data: body,
  });

export const putFoundationInstitution = (
  id: string,
  body: Record<string, unknown>
): Promise<FoundationApplicationDetail> =>
  goRequest({
    url: `${base}/applications/${id}/institution`,
    method: 'PUT',
    data: body,
  });

export const completeFoundationStudentVerification = (
  id: string,
  body?: { answers?: FoundationFormAnswerInput[] }
): Promise<FoundationApplicationDetail> =>
  goRequest({
    url: `${base}/applications/${id}/student-verification`,
    method: 'POST',
    data: body ?? {},
  });

export const deleteFoundationAnswerGroup = (
  id: string,
  formKind: string,
  groupId: string
): Promise<FoundationApplicationDetail> =>
  goRequest({
    url: `${base}/applications/${id}/forms/${encodeURIComponent(formKind)}/answer-groups/${encodeURIComponent(groupId)}`,
    method: 'DELETE',
  });

export const finalApproveFoundationApplication = (
  id: string,
  body: Record<string, unknown>
): Promise<FoundationApplicationDetail> =>
  goRequest({
    url: `${base}/applications/${id}/final-approval`,
    method: 'POST',
    data: body,
  });

export const listFoundationDisbursements = (params?: {
  status?: string;
  academic_year?: string;
}): Promise<{ items: FoundationDisbursement[] }> => {
  const search = new URLSearchParams();
  if (params?.status) search.set('status', params.status);
  if (params?.academic_year) search.set('academic_year', params.academic_year);
  const q = search.toString();
  return goRequest({
    url: `${base}/disbursements${q ? `?${q}` : ''}`,
    method: 'GET',
  });
};

export const exportFoundationDisbursementsToMip = (ids: string[]) =>
  goRequest({
    url: `${base}/disbursements/export-to-mip`,
    method: 'POST',
    data: { ids },
  });

export const revertFoundationDisbursementExport = (id: string) =>
  goRequest({
    url: `${base}/disbursements/${id}/revert-export`,
    method: 'POST',
  });

export const markFoundationDisbursementPaid = (
  id: string,
  payment_reference: string
) =>
  goRequest({
    url: `${base}/disbursements/${id}/mark-paid`,
    method: 'POST',
    data: { payment_reference },
  });

export const searchFoundationInstitutions = (
  q: string
): Promise<{ items: FoundationInstitutionSearchResult[] }> =>
  goRequest({
    url: `${base}/institutions/search?q=${encodeURIComponent(q)}`,
    method: 'GET',
  });

export const getFoundationFormDefinitions = (params?: {
  form_kind?: string;
  academic_year?: string;
}): Promise<{ items: FoundationFormDefinition[] }> => {
  const search = new URLSearchParams();
  if (params?.form_kind) search.set('form_kind', params.form_kind);
  if (params?.academic_year) search.set('academic_year', params.academic_year);
  const qs = search.toString();
  return goRequest({
    url: `${base}/form-definitions${qs ? `?${qs}` : ''}`,
    method: 'GET',
  });
};

export const saveFoundationACH = (
  id: string,
  body: { plaintext?: string; token?: string; last4: string }
): Promise<{ token: string; last4: string; stored_on_payees: boolean }> =>
  goRequest({
    url: `${base}/applications/${id}/ach`,
    method: 'POST',
    data: body,
  });

export const uploadFoundationEnrollmentDocument = (
  id: string,
  file: File
): Promise<{
  id: string;
  kind: string;
  file_name: string;
  uploaded_at: string;
}> => {
  const form = new FormData();
  form.append('file', file);
  return goRequest({
    url: `${base}/applications/${id}/documents`,
    method: 'POST',
    data: form,
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

/** Convert GET answers into PATCH/POST answer inputs. */
export const toFormAnswerInputs = (
  rows?: FoundationFormAnswer[]
): FoundationFormAnswerInput[] =>
  (rows || []).map((a) => ({
    question_key: a.question_key,
    value: a.value_text || '',
    ...(a.answer_group_id ? { answer_group_id: a.answer_group_id } : {}),
  }));
